import type { AssetRopaReview } from 'config/api/ropaReviews/ropaReviews.types';
import processing_activities_messages from 'messages/processing_activities_messages';

import type { AssetRopaReviewFilterOptions, AssetRopaReviewFilters } from './assetRopaReview.types';
import { hasAssetWarnings } from './assetRopaReviewHelpers';

export type AssetRopaTableFilterChip = {
  id: string;
  name: string | string[];
};

export type AssetRopaTableFilterState = {
  warning_state?: AssetRopaTableFilterChip[];
  dpia_state?: AssetRopaTableFilterChip[];
  sensitive_personal_data?: AssetRopaTableFilterChip[];
  third_country_processing?: AssetRopaTableFilterChip[];
};

export type AssetRopaTableAvailableFilters = {
  warning_state: {
    sectionName: string[];
    filters: AssetRopaTableFilterChip[];
    isSingleSelect: boolean;
  };
  dpia_state: {
    sectionName: string[];
    filters: AssetRopaTableFilterChip[];
    isSingleSelect: boolean;
  };
  sensitive_personal_data: {
    sectionName: string[];
    filters: AssetRopaTableFilterChip[];
    isSingleSelect: boolean;
  };
  third_country_processing: {
    sectionName: string[];
    filters: AssetRopaTableFilterChip[];
    isSingleSelect: boolean;
  };
};

const WARNING_STATE_FILTER_OPTIONS = ['with_warnings', 'no_warnings'] as const;
const DPIA_STATE_FILTER_OPTIONS = ['indicated', 'required_unapproved', 'none'] as const;
const BOOLEAN_FILTER_OPTIONS = ['true', 'false'] as const;

const warningStateMessage = (value: typeof WARNING_STATE_FILTER_OPTIONS[number]): string | string[] => {
  if (value === 'with_warnings') {
    return processing_activities_messages.asset_ropa.filters.with_warnings;
  }

  return processing_activities_messages.asset_ropa.filters.no_warnings;
};

const dpiaStateMessage = (value: typeof DPIA_STATE_FILTER_OPTIONS[number]): string | string[] => {
  const messageMap = {
    indicated: processing_activities_messages.asset_ropa.filters.dpia_indicated,
    required_unapproved: processing_activities_messages.asset_ropa.filters.dpia_required_unapproved,
    none: processing_activities_messages.asset_ropa.filters.dpia_none,
  } as const;

  return messageMap[value];
};

const booleanFilterMessage = (value: typeof BOOLEAN_FILTER_OPTIONS[number]): string | string[] =>
  processing_activities_messages.asset_ropa.filters[value === 'true' ? 'yes' : 'no'];

export const buildAssetRopaTableAvailableFilters = (): AssetRopaTableAvailableFilters => ({
  warning_state: {
    sectionName: processing_activities_messages.asset_ropa.filters.warning_state,
    filters: WARNING_STATE_FILTER_OPTIONS.map(value => ({
      id: value,
      name: warningStateMessage(value),
    })),
    isSingleSelect: true,
  },
  dpia_state: {
    sectionName: processing_activities_messages.asset_ropa.filters.dpia_state,
    filters: DPIA_STATE_FILTER_OPTIONS.map(value => ({
      id: value,
      name: dpiaStateMessage(value),
    })),
    isSingleSelect: true,
  },
  sensitive_personal_data: {
    sectionName: processing_activities_messages.asset_ropa.filters.sensitive_personal_data,
    filters: BOOLEAN_FILTER_OPTIONS.map(value => ({
      id: value,
      name: booleanFilterMessage(value),
    })),
    isSingleSelect: true,
  },
  third_country_processing: {
    sectionName: processing_activities_messages.asset_ropa.filters.third_country_processing,
    filters: BOOLEAN_FILTER_OPTIONS.map(value => ({
      id: value,
      name: booleanFilterMessage(value),
    })),
    isSingleSelect: true,
  },
});

export const assetRopaFilterValuesToTableFilters = (filters: AssetRopaReviewFilters): AssetRopaTableFilterState => {
  const tableFilters: AssetRopaTableFilterState = {};

  if (filters.warningState) {
    tableFilters.warning_state = [{ id: filters.warningState, name: warningStateMessage(filters.warningState) }];
  }

  if (filters.dpiaState) {
    tableFilters.dpia_state = [{ id: filters.dpiaState, name: dpiaStateMessage(filters.dpiaState) }];
  }

  if (filters.sensitivePersonalData) {
    tableFilters.sensitive_personal_data = [
      { id: filters.sensitivePersonalData, name: booleanFilterMessage(filters.sensitivePersonalData) },
    ];
  }

  if (filters.thirdCountryProcessing) {
    tableFilters.third_country_processing = [
      { id: filters.thirdCountryProcessing, name: booleanFilterMessage(filters.thirdCountryProcessing) },
    ];
  }

  return tableFilters;
};

export const mergeTableFiltersIntoAssetRopaFilters = (
  filters: AssetRopaReviewFilters,
  tableFilters: AssetRopaTableFilterState,
): AssetRopaReviewFilters => ({
  ...filters,
  warningState: (tableFilters.warning_state?.[0]?.id as AssetRopaReviewFilters['warningState']) ?? '',
  dpiaState: (tableFilters.dpia_state?.[0]?.id as AssetRopaReviewFilters['dpiaState']) ?? '',
  sensitivePersonalData: (tableFilters.sensitive_personal_data?.[0]?.id as AssetRopaReviewFilters['sensitivePersonalData']) ?? '',
  thirdCountryProcessing: (tableFilters.third_country_processing?.[0]?.id as AssetRopaReviewFilters['thirdCountryProcessing']) ?? '',
});

const compareStrings = (left: string, right: string) => left.localeCompare(right, undefined, { sensitivity: 'base' });

export const buildAssetRopaReviewFilterOptions = (assets: AssetRopaReview[]): AssetRopaReviewFilterOptions => {
  const assetTypeMap = new Map<number, string>();
  const ownerMap = new Map<number, string>();

  assets.forEach(asset => {
    assetTypeMap.set(asset.assetTypeId, asset.assetTypeName);
    if (asset.operationalOwnerMembershipId && asset.operationalOwnerName) {
      ownerMap.set(asset.operationalOwnerMembershipId, asset.operationalOwnerName);
    }
  });

  return {
    assetTypes: Array.from(assetTypeMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((left, right) => compareStrings(left.name, right.name)),
    operationalOwners: Array.from(ownerMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((left, right) => compareStrings(left.name, right.name)),
  };
};

export const filterAssetRopaReviews = (assets: AssetRopaReview[], filters: AssetRopaReviewFilters): AssetRopaReview[] => {
  const normalizedSearch = filters.search.trim().toLowerCase();

  return assets.filter(asset => {
    const matchesSearch =
      !normalizedSearch ||
      asset.assetName.toLowerCase().includes(normalizedSearch) ||
      (asset.operationalOwnerName?.toLowerCase().includes(normalizedSearch) ?? false);

    const matchesReviewStatus = !filters.reviewStatus || asset.reviewStatus === filters.reviewStatus;
    const matchesAssetType = !filters.assetTypeId || asset.assetTypeId === filters.assetTypeId;
    const matchesOwner =
      !filters.operationalOwnerMembershipId || asset.operationalOwnerMembershipId === filters.operationalOwnerMembershipId;

    const assetHasWarnings = hasAssetWarnings(asset.warnings);
    const matchesWarningState =
      !filters.warningState ||
      (filters.warningState === 'with_warnings' && assetHasWarnings) ||
      (filters.warningState === 'no_warnings' && !assetHasWarnings);

    const matchesDpiaState =
      !filters.dpiaState ||
      (filters.dpiaState === 'indicated' && asset.warnings.dpiaIndicated) ||
      (filters.dpiaState === 'required_unapproved' && asset.warnings.dpiaRequiredUnapproved) ||
      (filters.dpiaState === 'none' && !asset.warnings.dpiaIndicated && !asset.warnings.dpiaRequiredUnapproved);

    const matchesSensitivePersonalData =
      !filters.sensitivePersonalData ||
      (filters.sensitivePersonalData === 'true' && asset.warnings.sensitivePersonalData) ||
      (filters.sensitivePersonalData === 'false' && !asset.warnings.sensitivePersonalData);

    const matchesThirdCountryProcessing =
      !filters.thirdCountryProcessing ||
      (filters.thirdCountryProcessing === 'true' && asset.warnings.thirdCountryProcessing) ||
      (filters.thirdCountryProcessing === 'false' && !asset.warnings.thirdCountryProcessing);

    return (
      matchesSearch &&
      matchesReviewStatus &&
      matchesAssetType &&
      matchesOwner &&
      matchesWarningState &&
      matchesDpiaState &&
      matchesSensitivePersonalData &&
      matchesThirdCountryProcessing
    );
  });
};

export const assetRopaReviewFiltersToQueryParams = (filters: AssetRopaReviewFilters) => ({
  ...(filters.search ? { search: filters.search } : {}),
  ...(filters.reviewStatus ? { review_status: filters.reviewStatus } : {}),
  ...(filters.assetTypeId ? { asset_type_id: filters.assetTypeId } : {}),
  ...(filters.operationalOwnerMembershipId ? { operational_owner_membership_id: filters.operationalOwnerMembershipId } : {}),
  ...(filters.warningState ? { warning_state: filters.warningState } : {}),
  ...(filters.dpiaState ? { dpia_state: filters.dpiaState } : {}),
  ...(filters.sensitivePersonalData ? { sensitive_personal_data: filters.sensitivePersonalData === 'true' } : {}),
  ...(filters.thirdCountryProcessing ? { third_country_processing: filters.thirdCountryProcessing === 'true' } : {}),
});
